import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Pagination from '@mui/material/Pagination';
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import {Filters, Preloader} from "@vedmarket/ui-kit";

import {
  useBaseCurrency,
  useGetBrokerServicesCompaniesForFilterQuery,
  useGetBrokerServicesSearchByOrderIdMutation,
  useParamsBrokerServices,
  useSetBrokerServicesSearchByOrderIdMutation,
  useUserBrokerService,
} from "../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";

import CompanyCard from "../company-card";
import {ADORNMENTS} from "../../../../../../../config/constants";
import {isCalculationViewModeError} from 'utils/helpers';

export const enum SORTING_OPTIONS {
    COMPANIES_DESK = 'cost_total',
    COMPANIES_ASC = '-cost_total',
}

export const enum RESULTS_COUNT {
    COUNT_10 = 10,
    COUNT_30 = 30,
    COUNT_45 = 45
}


type FilterValues = {
  cost: [number, number]
  brokerIds: number[]
}

const ModalSearch = ({ preloadCompanies, handleChoose}: CustomsBrokerSearchProps) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ companies, setCompanies ] = React.useState<BrokerCompany[]>(preloadCompanies?.results || []);
  const [ totalCompanies, setTotalCompanies ] = React.useState(preloadCompanies?.results.length);
  const [ minTotal, setMinTotal ] = React.useState(preloadCompanies?.meta?.min_cost || 0);
  const [ maxTotal, setMaxTotal ] = React.useState(preloadCompanies?.meta?.max_cost || 1000);
  const [ filtersValues, setFiltersValues ] = React.useState<FilterValues>({
    cost: [ preloadCompanies?.meta?.min_cost || 0, preloadCompanies?.meta?.max_cost || 1000 ],
    brokerIds: [],
  });

  const [ showProgressBar, setShowProgressBar ] = useState(true);
  const [ filtersAreApplying, setFiltersAreApplying ] = useState(false);

  const [ getBrokerServices ] = useGetBrokerServicesSearchByOrderIdMutation()
  const [ setBrokerServices ] = useSetBrokerServicesSearchByOrderIdMutation()
  const { data: companiesList } = useGetBrokerServicesCompaniesForFilterQuery(orderId)

  const filterData = useParamsBrokerServices();
  const userBrokerService = useUserBrokerService()
  const baseCurrency = useBaseCurrency();

  useEffect(() => {
    if (preloadCompanies?.results){
      setCompanies(preloadCompanies?.results);
      setTotalCompanies(preloadCompanies?.count || preloadCompanies?.results.length);
    }
    if (preloadCompanies){
      const nextMinTotal = preloadCompanies.meta?.min_cost || 0;
      const nextMaxTotal = preloadCompanies.meta?.max_cost || 1000;

      setMinTotal(nextMinTotal);
      setMaxTotal(nextMaxTotal);
      setFiltersValues((state) => ({
        ...state,
        cost: [ nextMinTotal, nextMaxTotal ],
      }));
    }
  }, [ preloadCompanies ])

  useEffect(() => {
    if (companiesList) {
      const companyIds = companiesList.map((item) => item.id);

      setFiltersValues((state) => ({
        ...state,
        brokerIds: companyIds,
      }));
    }
  }, [ companiesList ])

  useEffect(() => {
    setTimeout(() => {
      setShowProgressBar(false)
    }, 1000);
  }, [ ])

  const companiesRequest = (data?: OrdersParamsBrokerService) => {
    return getBrokerServices({
      orderId,
      currency: baseCurrency,
      ...filterData,
      ...data,
    }).then((data) => {
      // @ts-ignore
      setCompanies(data.data.results);
      // @ts-ignore
      setTotalCompanies(data.data.count);
    });
  }

  const handleChangeResultsCount = (value: string | RESULTS_COUNT ) => companiesRequest({
    limit: Number(value)
  });

  const handleChangeCurrentPage = (event: React.ChangeEvent<unknown>, pageNumber: number) => companiesRequest({
    page: pageNumber
  });

  const handleSave = async (id: number) => {
    const result = await setBrokerServices({
      orderId: orderId,
      tariff_id: id
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    handleChoose();
  }

  const resultsCount = filterData.limit || RESULTS_COUNT.COUNT_30;

  const companiesToShow = companies.filter(item => item.tariff_id != userBrokerService?.tariff_id);
  const brokerIds = companiesList?.map((item) => item.id) || [];
  const brokerOptions = companiesList?.map((item) => ({
    value: item.id,
    label: item.name,
  })) || [];

  const handleChangeFilters = (values: FilterValues) => {
    setFiltersValues(values);
  }

  const handleApplyFilters = (values: FilterValues) => {
    const isDefaultCost = values.cost[0] === minTotal && values.cost[1] === maxTotal;
    const hasDefaultBrokerIds = values.brokerIds.length === brokerIds.length
      && brokerIds.every((item) => values.brokerIds.includes(item));

    setFiltersAreApplying(true);

    companiesRequest({
      cost_total_min: isDefaultCost ? undefined : values.cost[0],
      cost_total_max: isDefaultCost ? undefined : values.cost[1],
      broker_id: hasDefaultBrokerIds ? undefined : values.brokerIds,
    }).finally(() => {
      setFiltersAreApplying(false);
    });
  }

  const renderCompanies = () => {
    if (filtersAreApplying) {
      return <Preloader />
    }

    return (<>
      {!totalCompanies && <div className='search-results__results'>
        <Typography variant='h6' gutterBottom sx={{marginTop: 1}}>
          {t('actions__message__searchNotFound')}
        </Typography>
      </div>}
      {companiesToShow.map((company, index) =>
        <CompanyCard key={index} handleChoose={handleSave} company={company}/>
      )}
      {Number(totalCompanies) > resultsCount && <Pagination
        count={Math.ceil(Number(totalCompanies) / resultsCount)}
        sx={{marginTop: 3}}
        size='large'
        onChange={handleChangeCurrentPage}
      />}
    </>)
  }

  return (
    <div className='modal-search-container'>
      <div className='search-results'>
        {showProgressBar ? <CircularProgress/> : (<div className='search-results__container'>
          <div className='search-results__results'>
            <div className='search-results__sorting'>
              <FormControl sx={{width: '324px'}}>
                <InputLabel id='sorting-select'>{t('common__text__sort')}</InputLabel>
                <Select
                  labelId='sorting-select'
                  id='sorting-select'
                  value={filterData.order_by || SORTING_OPTIONS.COMPANIES_DESK}
                  label={t('common__text__sort')}
                  onChange={({target: {value}}) => companiesRequest({
                    order_by: value
                  })}
                  className='search-results__select-sorting'>
                  <MenuItem
                    value={SORTING_OPTIONS.COMPANIES_DESK}>{t('dictionaries__text__certification__priceAscendingOrder')}</MenuItem>
                  <MenuItem
                    value={SORTING_OPTIONS.COMPANIES_ASC}>{t('dictionaries__text__certification__priceDescendingOrder')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel id='count-select'>{t('common__text__resultsCount')}</InputLabel>
                <Select
                  labelId='count-select'
                  id='count-select'
                  value={resultsCount}
                  label={t('common__text__resultsCount')}
                  onChange={({target: {value}}) => handleChangeResultsCount(value)}
                  className='search-results__select-count'>
                  <MenuItem value={RESULTS_COUNT.COUNT_10}>10</MenuItem>
                  <MenuItem value={RESULTS_COUNT.COUNT_30}>30</MenuItem>
                  <MenuItem value={RESULTS_COUNT.COUNT_45}>45</MenuItem>
                </Select>
              </FormControl>
            </div>
            {userBrokerService && (<>
              <Typography variant='h6' className='customs-title__name' sx={{marginBottom: 3}}>
                {t('calc__text__delivery__yourChoice')}
              </Typography>
              <CompanyCard company={userBrokerService} isActive/>
            </>)}
            <Divider sx={{marginBottom: 3}}/>
            {renderCompanies()}
          </div>
          <Card className='search-results__filters'>
            <CardContent sx={{
              padding: 0,
              '&:last-child': {
                paddingBottom: 0
              }
            }}>
              <Typography variant='h6' gutterBottom sx={{padding: '16px 24px 0'}}>
                {t('common__text__filters')}
              </Typography>
              <Filters
                applyButtonText={t('actions__text__applyFilters')}
                onApply={(values) => handleApplyFilters(values as FilterValues)}
                onChange={(values) => handleChangeFilters(values as FilterValues)}
                sections={[ {
                  type: 'range',
                  key: 'cost',
                  title: t('dictionaries__text__certification__costOfServices'),
                  defaultValues: [ minTotal, maxTotal ],
                  min: minTotal,
                  max: maxTotal,
                  selectedValues: filtersValues.cost,
                  fromLabel: t('calc__text__priceFrom'),
                  toLabel: t('calc__text__priceTo'),
                  valuePrefix: 'USD',
                  inputAdornment: ADORNMENTS.PRICE,
                }, {
                  key: 'brokerIds',
                  title: t('dictionaries__text__additional__serviceProviders'),
                  defaultValues: brokerIds,
                  selectedValues: filtersValues.brokerIds,
                  options: brokerOptions,
                } ]}
              />
            </CardContent>
          </Card>
        </div>)}
      </div>
    </div>
  )
}

export default ModalSearch
